import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { toast } from 'react-toastify';
import { 
  Plus, Search, Edit2, Trash2, Users,
  Building2, UserCheck, Calendar, ToggleRight, ToggleLeft, ChevronLeft, ChevronRight
} from 'lucide-react';
import api from '../components/api';
import Modal from '../components/Modal';
import FormInput from '../components/FormInput';
import { useNavigate } from 'react-router-dom';
import { getDeleteConfirmation } from '../utils/confirmationUtils';
import ConfirmationModal from '../components/ConfirmationModal';

const DepartementPage = () => {
  const navigate = useNavigate();
  const [departements, setDepartements] = useState([]);
  const [personnels, setPersonnels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedDepartement, setSelectedDepartement] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    libelle: '',
    description: '',
    responsable: '',
  });
  const [formErrors, setFormErrors] = useState({});
  
  // États pour la confirmation de suppression
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [departementToDelete, setDepartementToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  // Ajouter les états pour la confirmation de désactivation
  const [isStatusConfirmModalOpen, setIsStatusConfirmModalOpen] = useState(false);
  const [departementToToggle, setDepartementToToggle] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [pagination, setPagination] = useState({
    count: 0,
    next: null,
    previous: null,
    current: 1,
    total_pages: 1
  });

  useEffect(() => {
    fetchDepartements();
  }, []);

  const fetchDepartements = async (page = 1) => {
    try {
      setLoading(true);
      const [departementsResponse, personnelsResponse] = await Promise.all([
        api.get('/etablissement/departements/', {
          params: {
            page: page,
            page_size: 10
          }
        }),
        api.get('/etablissement/personnels/')
      ]);
      
      // Filtrer pour n'avoir que les personnels actifs
      const personnelsActifs = personnelsResponse.data.results ? 
        personnelsResponse.data.results.filter(p => p.is_active) :
        personnelsResponse.data.filter(p => p.is_active);
      
      setDepartements(departementsResponse.data.results);
      setPersonnels(personnelsActifs);
      setPagination({
        count: departementsResponse.data.count,
        next: departementsResponse.data.next,
        previous: departementsResponse.data.previous,
        current: page,
        total_pages: Math.ceil(departementsResponse.data.count / 10)
      });
      setLoading(false);
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    
    // Clear error when field is edited
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: undefined
      });
    }
  };

  const handleOpenModal = (departement = null) => {
    if (departement) {
      setFormData({
        libelle: departement.libelle || '',
        description: departement.description || '',
        responsable: departement.responsable?.id || '',
      });
      setSelectedDepartement(departement);
    } else {
      setFormData({
        libelle: '',
        description: '',
        responsable: '',
      });
      setSelectedDepartement(null);
    }
    setFormErrors({});
    setIsModalOpen(true);
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.libelle.trim()) {
      errors.libelle = 'Le libellé du département est requis';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    try {
      if (selectedDepartement) {
        await api.put(`/etablissement/departements/${selectedDepartement.id}/`, formData);
        toast.success('Département modifié avec succès');
      } else {
        await api.post('/etablissement/departements/', formData);
        toast.success('Département créé avec succès');
      }
      setIsModalOpen(false);
      fetchDepartements();
    } catch (error) {
      if (error.response?.data) {
        const apiErrors = {};
        Object.entries(error.response.data).forEach(([field, messages]) => {
          apiErrors[field] = Array.isArray(messages) ? messages[0] : messages;
        });
        setFormErrors(apiErrors);
      } else {
        toast.error('Erreur lors de la sauvegarde du département');
      }
    }
  };

  const handleOpenDeleteModal = (departement) => {
    setDepartementToDelete(departement);
    setIsConfirmModalOpen(true);
  };

  const handleDelete = async () => {
    if (!departementToDelete) return;
    
    setDeleteLoading(true);
    try {
      await api.delete(`/etablissement/departements/${departementToDelete.id}/`);
      toast.success('Département supprimé avec succès');
      fetchDepartements();
      setIsConfirmModalOpen(false);
      setDepartementToDelete(null);
    } catch (error) {
      toast.error('Erreur lors de la suppression du département');
    } finally {
      setDeleteLoading(false);
    }
  };

  const confirmConfig = departementToDelete 
    ? getDeleteConfirmation(departementToDelete, 'le département', 'libelle', 'supprimer') 
    : {};

  const filteredDepartements = departements.filter(departement =>
    departement.libelle.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (departement.responsable_nom || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const renderModalFooter = () => (
    <div className="flex justify-end space-x-3">
      <button
        type="button"
        onClick={() => setIsModalOpen(false)}
        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#202654]"
      >
        Annuler
      </button>
      <button
        type="submit"
        onClick={handleSubmit}
        className="px-4 py-2 text-sm font-medium text-white bg-[#202654] border border-transparent rounded-md shadow-sm hover:bg-[#202654]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#202654]"
      >
        {selectedDepartement ? 'Modifier' : 'Créer'}
      </button>
    </div>
  );

  const handleToggleStatusClick = (departement) => {
    setDepartementToToggle(departement);
    setIsStatusConfirmModalOpen(true);
  };

  const handleToggleStatus = async () => {
    if (!departementToToggle) return;
    
    setStatusLoading(true);
    try {
      const response = await api.put(`/etablissement/departements/${departementToToggle.id}/`, {
        ...departementToToggle,
        is_active: !departementToToggle.is_active
      });
      
      if (response.data) {
        toast.success(`Le département a été ${departementToToggle.is_active ? 'désactivé' : 'activé'} avec succès`);
        fetchDepartements();
      }
      setIsStatusConfirmModalOpen(false);
      setDepartementToToggle(null);
    } catch (error) {
      toast.error('Erreur lors du changement de statut du département');
    } finally {
      setStatusLoading(false);
    }
  };

  const getStatusConfirmConfig = () => {
    if (!departementToToggle) return {};
    
    const action = departementToToggle.is_active ? 'désactiver' : 'activer';
    return {
      title: `${departementToToggle.is_active ? 'Désactiver' : 'Activer'} le département`,
      message: `Êtes-vous sûr de vouloir ${action} le département "${departementToToggle.libelle}" ?${
        departementToToggle.is_active && departementToToggle.nombre_personnel > 0
          ? `\n\nAttention : Ce département contient ${departementToToggle.nombre_personnel} membre(s).`
          : ''
      }`,
      confirmText: departementToToggle.is_active ? 'Désactiver' : 'Activer',
      type: departementToToggle.is_active ? 'warning' : 'success'
    };
  };

  const handlePageChange = (newPage) => {
    fetchDepartements(newPage);
  };

  // Composant de pagination
  const Pagination = () => (
    <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
      <div className="flex flex-1 justify-between sm:hidden">
        <button
          onClick={() => handlePageChange(pagination.current - 1)}
          disabled={!pagination.previous}
          className={`relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 ${
            !pagination.previous 
              ? 'opacity-50 cursor-not-allowed'
              : 'hover:bg-gray-50'
          }`}
        >
          Précédent
        </button>
        <button
          onClick={() => handlePageChange(pagination.current + 1)}
          disabled={!pagination.next}
          className={`relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 ${
            !pagination.next 
              ? 'opacity-50 cursor-not-allowed'
              : 'hover:bg-gray-50'
          }`}
        >
          Suivant
        </button>
      </div>
      <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
        <div>
          <p className="text-sm text-gray-700">
            Affichage de{' '}
            <span className="font-medium">
              {((pagination.current - 1) * 10) + 1}
            </span>{' '}
            à{' '}
            <span className="font-medium">
              {Math.min(pagination.current * 10, pagination.count)}
            </span>{' '}
            sur{' '}
            <span className="font-medium">{pagination.count}</span> départements
          </p>
        </div>
        <div>
          <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
            <button
              onClick={() => handlePageChange(pagination.current - 1)}
              disabled={!pagination.previous}
              className={`relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 ${
                !pagination.previous 
                  ? 'opacity-50 cursor-not-allowed'
                  : 'hover:bg-gray-50'
              }`}
            >
              <span className="sr-only">Précédent</span>
              <ChevronLeft className="h-5 w-5" aria-hidden="true" />
            </button>
            {[...Array(pagination.total_pages)].map((_, i) => (
              <button
                key={i + 1}
                onClick={() => handlePageChange(i + 1)}
                className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${
                  pagination.current === i + 1
                    ? 'z-10 bg-[#202654] text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#202654]'
                    : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50'
                }`}
              >
                {i + 1}
              </button>
            ))}
            <button
              onClick={() => handlePageChange(pagination.current + 1)}
              disabled={!pagination.next}
              className={`relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 ${
                !pagination.next 
                  ? 'opacity-50 cursor-not-allowed'
                  : 'hover:bg-gray-50'
              }`}
            >
              <span className="sr-only">Suivant</span>
              <ChevronRight className="h-5 w-5" aria-hidden="true" />
            </button>
          </nav>
        </div>
      </div>
    </div>
  );

  return (
    <Layout>
      <div className="container mx-auto px-4 py-6">
        {/* Header avec bannière */}
        <div className="bg-gradient-to-r from-[#202654] to-[#3a4999] rounded-xl p-6 mb-8 shadow-lg">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div className="flex items-center text-white">
              <button
                onClick={() => navigate(-1)}
                className="mr-4 p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-all duration-200 backdrop-blur-sm"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
              <div>
                <h1 className="text-2xl font-bold">Gestion des Départements</h1>
                <p className="text-white opacity-80 mt-1">
                  Gérez les départements et leurs responsables
                </p>
              </div>
            </div>
            <div className="flex gap-4 w-full md:w-auto">
              <div className="relative flex-grow md:flex-grow-0">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Rechercher un département..."
                  className="pl-10 pr-4 py-2 w-full md:w-64 bg-white/90 backdrop-blur-sm text-gray-900 rounded-lg"
                />
              </div>
              <button
                onClick={() => handleOpenModal()}
                className="inline-flex items-center bg-white text-[#202654] px-4 py-2 rounded-lg shadow-md hover:bg-gray-100 transition-all duration-200 whitespace-nowrap"
              >
                <Plus className="h-5 w-5 mr-2" />
                Nouveau Département
              </button>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="flex justify-center items-center p-8">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#202654]"></div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Département
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Responsable
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Personnel
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredDepartements.length === 0 ? (
                    <tr>
                      <td colSpan="4" className="px-6 py-12">
                        <div className="flex flex-col items-center justify-center text-center">
                          <Building2 className="h-12 w-12 text-gray-400 mb-4" />
                          <h3 className="text-lg font-medium text-gray-900 mb-1">Aucun département trouvé</h3>
                          <p className="text-sm text-gray-500 max-w-md">
                            {searchTerm ? 'Essayez avec un autre terme de recherche' : 'Commencez par créer votre premier département'}
                          </p>
                          {!searchTerm && (
                            <button
                              onClick={() => handleOpenModal()}
                              className="mt-6 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#202654] hover:bg-[#202654]/90"
                            >
                              <Plus className="h-5 w-5 mr-2" />
                              Créer un département
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ) : (
                    filteredDepartements.map((departement) => (
                      <tr key={departement.id} className={`hover:bg-gray-50 transition-colors ${!departement.is_active ? 'bg-gray-50' : ''}`}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className={`h-9 w-9 rounded-full ${
                              departement.is_active 
                                ? 'bg-[#202654]/10' 
                                : 'bg-gray-200'
                            } flex items-center justify-center mr-3`}>
                              <Building2 className={`h-5 w-5 ${
                                departement.is_active 
                                  ? 'text-[#202654]' 
                                  : 'text-gray-400'
                              }`} />
                            </div>
                            <div>
                              <div className={`text-sm font-medium ${
                                departement.is_active 
                                  ? 'text-gray-900' 
                                  : 'text-gray-500'
                              }`}>
                                {departement.libelle}
                              </div>
                              <div className="text-xs text-gray-500">
                                {departement.is_active ? 'Actif' : 'Inactif'}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {departement.responsable_nom ? (
                            <div className="flex items-center">
                              <UserCheck className="h-5 w-5 text-green-500 mr-2" />
                              <span className="text-sm text-gray-900">{departement.responsable_nom}</span>
                            </div>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                              Non défini
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <Users className="h-5 w-5 text-gray-400 mr-2" />
                            <span className="text-sm text-gray-900 flex items-center">
                              <span className={`px-2.5 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                departement.nombre_personnel > 0 
                                  ? 'bg-blue-100 text-blue-800' 
                                  : 'bg-gray-100 text-gray-800'
                              }`}>
                                {departement.nombre_personnel} membre{departement.nombre_personnel !== 1 ? 's' : ''}
                              </span>
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex justify-end space-x-2">
                            <button
                              onClick={() => handleToggleStatusClick(departement)}
                              className={`text-${departement.is_active ? 'red' : 'green'}-600 hover:text-${departement.is_active ? 'red' : 'green'}-900 p-1 rounded-full hover:bg-${departement.is_active ? 'red' : 'green'}-50 transition-colors`}
                              title={departement.is_active ? 'Désactiver' : 'Activer'}
                            >
                              {departement.is_active ? (
                                <ToggleRight className="h-5 w-5" />
                              ) : (
                                <ToggleLeft className="h-5 w-5" />
                              )}
                            </button>
                            <button
                              onClick={() => handleOpenModal(departement)}
                              className="text-indigo-600 hover:text-indigo-900 p-1 rounded-full hover:bg-indigo-50 transition-colors"
                              title="Modifier"
                            >
                              <Edit2 className="h-5 w-5" />
                            </button>
                            <button
                              onClick={() => handleOpenDeleteModal(departement)}
                              className="text-red-600 hover:text-red-900 p-1 rounded-full hover:bg-red-50 transition-colors"
                              title="Supprimer"
                            >
                              <Trash2 className="h-5 w-5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
          {!loading && <Pagination />}
        </div>
      </div>

      {/* Modal pour création/édition de département */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={selectedDepartement ? 'Modifier le département' : 'Nouveau département'}
        footer={renderModalFooter()}
      >
        <form onSubmit={handleSubmit} className="space-y-6">
          <FormInput
            label="Libellé du département"
            type="text"
            name="libelle"
            value={formData.libelle}
            onChange={handleInputChange}
            placeholder="Entrez le nom du département"
            required
            error={formErrors.libelle}
            icon={Building2}
          />
          
          <FormInput
            label="Description"
            type="textarea"
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            placeholder="Description du département"
            rows={3}
            error={formErrors.description}
          />
          
          <FormInput
            label="Responsable"
            type="select"
            name="responsable"
            value={formData.responsable}
            onChange={handleInputChange}
            icon={UserCheck}
            options={[
              { value: '', label: 'Sélectionner un responsable' },
              ...personnels
                .filter(p => p.is_active)
                .map(personnel => ({
                  value: personnel.id,
                  label: personnel.nom + ' ' + personnel.prenom
                }))
            ]}
            helpText="Facultatif. Vous pouvez désigner un responsable pour ce département."
            error={formErrors.responsable}
          />
        </form>
      </Modal>

      {/* Modal de confirmation de suppression */}
      <ConfirmationModal 
        isOpen={isConfirmModalOpen}
        onClose={() => setIsConfirmModalOpen(false)}
        onConfirm={handleDelete}
        title={confirmConfig.title || "Supprimer le département"}
        message={confirmConfig.message || "Êtes-vous sûr de vouloir supprimer ce département ?"}
        confirmText={confirmConfig.confirmText || "Supprimer"}
        cancelText="Annuler"
        type={confirmConfig.type || "danger"}
        isLoading={deleteLoading}
      />

      {/* Modal de confirmation de changement de statut */}
      <ConfirmationModal 
        isOpen={isStatusConfirmModalOpen}
        onClose={() => setIsStatusConfirmModalOpen(false)}
        onConfirm={handleToggleStatus}
        title={getStatusConfirmConfig().title}
        message={getStatusConfirmConfig().message}
        confirmText={getStatusConfirmConfig().confirmText}
        cancelText="Annuler"
        type={getStatusConfirmConfig().type}
        isLoading={statusLoading}
      />
    </Layout>
  );
};

export default DepartementPage;
